import { AnalyticsContext } from '../../../../Pages/GMS/Performance/Context/AnalyticsContext';
import AuthManager from "Common/AuthManager";
import BootBoxManager from "Common/BootBoxManager";
import PermissionsManager from "Common/PermissionsManager";
import Utils from "Common/Utils";
import { AppContext } from "Context/AppContext";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import React, { useContext, useEffect, useCallback } from "react";
import { useNavigate, useRouteLoaderData } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { IIndustryList, IParty, IPartyList } from "../types";
import { getMerchantsBasedOnPartyAndIndustry } from '../APIServices/GMSServices';
import { IRefreshHierarchy } from 'types/Context/IRefreshHierarchy';
import { WorkspaceBootstrapResult, isWorkspaceNotReadyRoute } from 'Route/workspaceBootstrap';

/**
 * Workspace selection (parent channel + environment) is stored PER TAB, not per browser.
 *
 * The same user must be able to keep two tabs on two different Environments at once. These keys
 * used to live in localStorage only, which is shared across every tab of the origin — so switching
 * the Environment in one tab overwrote the other tab's selection, and the second tab's header fell
 * back to "-" (reproduced 2026-07-31: tab A on channle-2, tab B switched to MidwayDemo, tab A lost
 * its environment).
 *
 * The scoping below mirrors what AuthManager already does for the auth user and the GMS token
 * (see AuthManager.getGmsToken / getAuthUser): write both, read sessionStorage first and fall back
 * to localStorage. A brand-new or duplicated tab therefore inherits the last-known selection as a
 * seed, then owns its own copy and is never disturbed by another tab again — which also preserves
 * the "tab duplication" behaviour the refreshAll/refreshChannels paths below rely on.
 *
 * sessionStorage.clear() in AuthManager.logoutUser still wipes the per-tab copy on logout.
 */
const readWorkspaceKey = (key: string): string | null =>
    sessionStorage.getItem(key) ?? localStorage.getItem(key);

const writeWorkspaceKey = (key: string, value: string) => {
    sessionStorage.setItem(key, value);
    // localStorage is only a seed for tabs opened later — the tab's own copy above always wins.
    localStorage.setItem(key, value);
};

const clearWorkspaceKey = (key: string) => {
    // Only this tab's copy. The localStorage seed is deliberately left alone: clearing it here
    // would wipe the value other tabs (and future new tabs) fall back to.
    sessionStorage.removeItem(key);
};

const useSearchBar = () => {
    const navigate = useNavigate();

    var [partyList, setPartyList] = React.useState<IPartyList>([]);
    var [industryList, setIndustryList] = React.useState<IIndustryList>([]);
    const appData = useContext<any>(AppContext);
    const analyticsContext = useContext<any>(AnalyticsContext);

    // Local state for selected party and industry for immediate UI feedback
    // (Context state propagates slower than local state)
    const [selectedParty, setSelectedPartyLocal] = React.useState<any>(null);
    const [selectedIndustry, setSelectedIndustryLocal] = React.useState<any>(null);

    const [parentChannelList, setParentChannelList] = React.useState<any[]>([]);
    const [selectedParentChannel, setSelectedParentChannel] = React.useState<any>(null);
    const [environmentList, setEnvironmentList] = React.useState<any[]>([]);
    const [selectedEnvironment, setSelectedEnvironment] = React.useState<any>(null);

    const [channelList, setChannelList] = React.useState<any[]>([])
    const [selectedChannel, setSelectedChannel] = React.useState('')

    const [isLoadingParty, setIsLoadingParty] = React.useState(false);
    const [isLoadingIndustry, setIsLoadingIndustry] = React.useState(false);
    const [isLoadingChannel, setIsLoadingChannel] = React.useState(false);
    const [isLoadingEnvironment, setIsLoadingEnvironment] = React.useState(false);
    
    const requestVersions = React.useRef(new RequestVersionManager());
    
    const operationInProgress = React.useRef({
        party: false,
        industry: false,
        channel: false,
        environment: false
    });
    
    const isManualPartyChange = React.useRef(false);
    const isManualIndustryChange = React.useRef(false);
    const isInitialLoad = React.useRef(true);
    const isSwitchingWorkspace = React.useRef(false);
    const hierarchyLoadedByRefresh = React.useRef(false);
    const prevCurrentPartyId = React.useRef<string | null | undefined>(undefined);
    const prevIndustryId = React.useRef<string | null | undefined>(undefined);
    
    const current_party_id = JSON.parse(localStorage?.getItem("current_party_id") as string)
    const industryId = appData.get_selected_industry() && appData?.get_selected_industry()?.id;

    // Helper function to sort lists alphabetically by name (case insensitive)
    const sortByName = <T extends { name?: string }>(list: T[]): T[] => {
        return [...list].sort((a, b) =>
            (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
        );
    };

    const refreshParties = async (): Promise<void> => {
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        
        try {
            let parties = await ServiceWrapper.doGet(
                api_base_url + "user/party/active/list",
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (parties.errorfound || !parties.data?.data) {
                console.error('[refreshParties] Error or no data in response');
                return;
            }
            
            const tplist = parties.data.data;
            if (tplist.length <= 0) {
                console.log('[refreshParties] Empty party list');
                return;
            }
            
            setPartyList(sortByName(tplist));
            
            const currentPartyId = appData.selectedParty?.id || localStorage.getItem("party");
            
            if (currentPartyId) {
                const partyStillExists = tplist.find((p: any) => p.id === currentPartyId);

                if (partyStillExists) {
                    setSelectedPartyLocal(partyStillExists);
                    appData.setSelectedParty(partyStillExists);
                    localStorage.setItem("party", partyStillExists.id);
                    await refreshDomains(partyStillExists.id);
                } else {
                    const firstParty = tplist[0];
                    setSelectedPartyLocal(firstParty);
                    appData.setSelectedParty(firstParty);
                    localStorage.setItem("party", firstParty.id);
                    await refreshDomains(firstParty.id);
                }
            } else {
                const firstParty = tplist[0];
                setSelectedPartyLocal(firstParty);
                appData.setSelectedParty(firstParty);
                localStorage.setItem("party", firstParty.id);
                await refreshDomains(firstParty.id);
            }
        } catch (error) {
            console.error('[refreshParties] Error:', error);
        }
    };

    const refreshDomains = async (partyId?: string, storedChannelId?: string | null, storedEnvId?: string | null): Promise<boolean> => {
        const targetPartyId = partyId || appData.selectedParty?.id;

        if (!targetPartyId) {
            console.log('[refreshDomains] No party ID, returning');
            return false;
        }

        const api_base_url = AppSettings.GMS_BACKEND_API_URL;

        try {
            let industryResponse = await ServiceWrapper.doGet(
                api_base_url + "party/" + targetPartyId + "/industry/list/active",
                null,
                null,
                AuthManager.getGmsToken()
            );


            if (industryResponse.errorfound || !industryResponse.data?.data) {
                console.log('[refreshDomains] Error or no data');
                return false;
            }

            const tplist = industryResponse.data.data;

            if (!tplist || tplist.length === 0) {
                setIndustryList([]);
                setSelectedIndustryLocal(null);
                appData.setSelectedIndustry(null);
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);
                localStorage.removeItem("industry");
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setMerchantList([]);
                return false;
            }

            setIndustryList(sortByName(tplist));

            let currentIndustryId = appData.selectedIndustry?.id;
            if (!currentIndustryId && localStorage.getItem("industry")) {
                try {
                    const parsed = JSON.parse(localStorage.getItem("industry")!.replace(/\\/g, ""));
                    currentIndustryId = parsed.id;
                } catch (e) {
                    console.error('[refreshDomains] Invalid industry JSON in localStorage:', e);
                }
            }

            let domainToSelect = null;
            if (currentIndustryId) {
                domainToSelect = tplist.find((i: any) => i.id === currentIndustryId);
            }

            if (!domainToSelect) {
                domainToSelect = tplist[0];
            }

            // Update local state for immediate UI feedback
            setSelectedIndustryLocal(domainToSelect);
            appData.update_industry(domainToSelect);
            localStorage.setItem("industry", JSON.stringify(domainToSelect));

            const channelsLoaded = await refreshChannels(targetPartyId, domainToSelect.id, storedChannelId, storedEnvId);
            return channelsLoaded;
        } catch (error) {
            console.error('[refreshDomains] Error:', error);
            return false;
        }
    };

    const refreshChannels = async (partyId?: string, domainId?: string, storedChannelId?: string | null, storedEnvId?: string | null): Promise<boolean> => {
        const targetPartyId = partyId || appData.selectedParty?.id;
        const targetDomainId = domainId || appData.selectedIndustry?.id;


        if (!targetPartyId || !targetDomainId) {
            console.log('[refreshChannels] Missing party or domain ID');
            return false;
        }

        try {
            const parentResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/industry/${targetDomainId}/parent_channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );

            const response = parentResponse?.data;

            if (!response || !response.data || !Array.isArray(response.data)) {
                setParentChannelList([]);
                setSelectedParentChannel(null);
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setChannelKey("");
                appData.setChannelName("");
                appData.setMerchantList([]);
                return false;
            }

            setParentChannelList(sortByName(response.data));

            if (response.data.length === 0) {
                setSelectedParentChannel(null);
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setChannelKey("");
                appData.setChannelName("");
                appData.setMerchantList([]);
                return false;
            }

            // Use storedChannelId parameter FIRST (for tab duplication), then fall back to state, then localStorage
            let currentChannelId = storedChannelId || selectedParentChannel?.id;
            if (!currentChannelId && readWorkspaceKey("selectedParentChannel")) {
                try {
                    const parsed = JSON.parse(readWorkspaceKey("selectedParentChannel")!);
                    currentChannelId = parsed.id;
                } catch (e) {
                    console.error('[refreshChannels] Invalid channel JSON in localStorage:', e);
                }
            }

            // Same caveat as refreshEnvironments below: nothing survives logout to restore the
            // user's last parent channel, so a fresh login falls through to channel[0].

            let channelToSelect = null;
            if (currentChannelId) {
                channelToSelect = response.data.find((c: any) => c.id === currentChannelId);
            }

            if (!channelToSelect) {
                channelToSelect = response.data[0];
            }

            setSelectedParentChannel(channelToSelect);
            writeWorkspaceKey("selectedParentChannel", JSON.stringify(channelToSelect));
            const envSuccess = await refreshEnvironments(channelToSelect.id, targetPartyId, storedEnvId);
            return envSuccess;
        } catch (error) {
            console.error('[refreshChannels] Error:', error);
            return false;
        }
    };

    const refreshEnvironments = async (channelId?: string, partyId?: string, storedEnvId?: string | null): Promise<boolean> => {
        const targetChannelId = channelId || selectedParentChannel?.id;
        const targetPartyId = partyId || appData.selectedParty?.id;

        if (!targetChannelId || !targetPartyId) {
            return false;
        }

        try {
            const environementResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/parent_channel/${targetChannelId}/channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );

            const response = environementResponse?.data;

            if (!response || !response.data || !Array.isArray(response.data)) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedEnvironment");
                appData.setChannelKey('');
                appData.setChannelName('');
                return false;
            }

            setEnvironmentList(sortByName(response.data));
            appData.setEnvironmentList?.(sortByName(response.data));

            if (response.data.length === 0) {
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedEnvironment");
                appData.setChannelKey('');
                appData.setChannelName('');
                return false;
            }

            // Use storedEnvId parameter FIRST (for tab duplication), then fall back to state, then localStorage
            let currentEnvId = storedEnvId || selectedEnvironment?.id;
            if (!currentEnvId && readWorkspaceKey("selectedEnvironment")) {
                try {
                    const parsed = JSON.parse(readWorkspaceKey("selectedEnvironment")!);
                    currentEnvId = parsed.id;
                } catch (e) {
                    console.error('[refreshEnvironments] Invalid environment JSON in localStorage:', e);
                }
            }
            // NOTE: on a fresh login none of the sources above resolve, so we fall through to
            // environment[0] below — the user is NOT returned to the environment they last used.
            // There is currently nowhere to read it from: AuthManager.getAuthUser() is empty at
            // this point because POST /login returns only {access_token} (no user_object), and the
            // backend never persists the selection anyway — route_handler.handle_login builds the
            // JWT identity from the SAMS profile alone and only /party/switch sets
            // current_channel_id, into a token that logout discards. Restoring it needs either
            // server-side per-user persistence returned by /login, or a logout-surviving
            // client-side key (AuthManager.logoutUser does a blanket localStorage.clear()).
            // Verified 2026-07-31: reading getAuthUser()?.current_channel_id here is always
            // undefined on fresh login, so don't re-add it without one of those two changes.

            let envToSelect = null;
            if (currentEnvId) {
                envToSelect = response.data.find((e: any) => e.id === currentEnvId);
            }

            if (!envToSelect) {
                envToSelect = response.data[0];
            }

            setSelectedEnvironment(envToSelect);
            writeWorkspaceKey("selectedEnvironment", JSON.stringify(envToSelect));
            appData.setChannelKey(envToSelect?.key ?? '');
            appData.setChannelName(envToSelect?.name ?? '');
            appData.setEnableConfigure(envToSelect?.enable_configure ?? false);
            appData.setEnvPlatform?.(envToSelect?.platform ?? '');
            return true;
        } catch (error) {
            console.error('[refreshEnvironments] Error:', error);
            return false;
        }
    };


    // The one-time party -> industry -> parent channel -> environment -> party/switch bootstrap
    // used to run here as refreshAll(), fired from a useEffect below guarded by isInitialLoad — a
    // ref that only survived as long as this component never remounted. It now runs in a route
    // loader (App.tsx's workspace-bootstrap route, backed by Route/workspaceBootstrap.ts), memoized
    // at module scope so it runs exactly once regardless of any component's lifetime. This effect
    // just applies that already-resolved result into local state / AppContext, once — the same
    // writes refreshAll used to make, sourced from the loader's data instead of fetching it itself.
    // refreshDomains/refreshChannels/refreshEnvironments/switchWorkspace below are untouched: they're
    // still called by refreshParties() (16 admin party/domain/channel screens depend on it) and by
    // the manual party/industry/parent-channel/environment switch handlers.
    const workspaceBootstrap = useRouteLoaderData("workspace-bootstrap") as WorkspaceBootstrapResult | undefined;

    React.useEffect(() => {
        if (!isInitialLoad.current || !workspaceBootstrap) {
            return;
        }
        isInitialLoad.current = false;

        if (!workspaceBootstrap.workspaceLoaded) {
            return;
        }

        setPartyList(workspaceBootstrap.partyList);
        setSelectedPartyLocal(workspaceBootstrap.party);
        appData.setSelectedParty(workspaceBootstrap.party);

        // The bootstrap skips parties whose workspace chain is incomplete and loads the next usable
        // one (workspaceBootstrap.ts). Landing in a different party than expected without a word
        // would look like data loss, so say which one(s) were passed over and where we ended up.
        const skipped = workspaceBootstrap.skippedParties ?? [];
        if (skipped.length > 0) {
            const names = skipped.map((s) => s.party?.name).filter(Boolean).join(", ");
            toast.info(
                `Workspace not available for ${names || "your default party"}. ` +
                `Loaded ${workspaceBootstrap.party?.name ?? "another workspace"} instead.`,
                // `duration`, not react-toastify's `autoClose` — the app is on the DS toast
                // store (design-system § 116) and react-toastify is no longer a dependency.
                { duration: 8000 }
            );
        }

        setIndustryList(workspaceBootstrap.industryList);
        setSelectedIndustryLocal(workspaceBootstrap.industry);
        appData.update_industry(workspaceBootstrap.industry);

        setParentChannelList(workspaceBootstrap.parentChannelList);
        setSelectedParentChannel(workspaceBootstrap.parentChannel);

        setEnvironmentList(workspaceBootstrap.environmentList);
        appData.setEnvironmentList?.(workspaceBootstrap.environmentList);
        setSelectedEnvironment(workspaceBootstrap.environment);
        appData.setChannelKey(workspaceBootstrap.environment?.key ?? '');
        appData.setChannelName(workspaceBootstrap.environment?.name ?? '');
        appData.setEnableConfigure(workspaceBootstrap.environment?.enable_configure ?? false);
        appData.setEnvPlatform?.(workspaceBootstrap.environment?.platform ?? '');

        // Same flag refreshAll used to set at the same logical point, so the partyList-triggered
        // effect below still skips its own redundant fetch after the initial bootstrap.
        hierarchyLoadedByRefresh.current = true;
    }, [workspaceBootstrap]);

    useEffect(() => {
        const refreshFunctions: IRefreshHierarchy = {
            refreshParties,
            refreshDomains,
            refreshChannels,
            refreshEnvironments
        };
        
        appData.setRefreshHierarchy(refreshFunctions);
    }, [selectedParentChannel, selectedEnvironment, partyList, industryList, parentChannelList, environmentList]);

    const validateStoredSelections = () => {
        const storedParty = localStorage.getItem("party");
        const storedIndustry = localStorage.getItem("industry");
        const storedChannel = readWorkspaceKey("selectedParentChannel");
        const storedEnv = readWorkspaceKey("selectedEnvironment");
        
        if (storedEnv && !storedChannel) {
            clearWorkspaceKey("selectedEnvironment");
        }
        
        if (storedChannel && !storedIndustry) {
            clearWorkspaceKey("selectedParentChannel");
            clearWorkspaceKey("selectedEnvironment");
        }
        
        if (storedIndustry && !storedParty) {
            localStorage.removeItem("industry");
            clearWorkspaceKey("selectedParentChannel");
            clearWorkspaceKey("selectedEnvironment");
        }
    };
    
    React.useEffect(() => {
        validateStoredSelections();
    }, []);

    function capitalizeFirstLetter(string: string) {
        return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
    }

    // Set username in context on mount (moved out of getUserName to avoid setState during render)
    React.useEffect(() => {
        let userObj = null;
        if (AuthManager.isAuthenticated()) userObj = AuthManager.getAuthUser();
        if (userObj?.profile) {
            appData.setUserName(capitalizeFirstLetter(userObj.profile.first_name) ?? '');
        }
    }, []);

    // Sync local state with context state (for external updates or initial load)
    React.useEffect(() => {
        if (appData.selectedParty && appData.selectedParty?.id !== selectedParty?.id) {
            setSelectedPartyLocal(appData.selectedParty);
        }
    }, [appData.selectedParty]);

    React.useEffect(() => {
        if (appData.selectedIndustry && appData.selectedIndustry?.id !== selectedIndustry?.id) {
            setSelectedIndustryLocal(appData.selectedIndustry);
        }
    }, [appData.selectedIndustry]);

    const getUserName = () => {
        let userObj = null;
        if (AuthManager.isAuthenticated()) userObj = AuthManager.getAuthUser();
        if (!userObj?.profile) return capitalizeFirstLetter("guest");
        return capitalizeFirstLetter(userObj.profile.first_name);
    };

    const getPartyList = async (skipWorkspaceSwitch: boolean = false) => {
        
        let _partyList: IPartyList;
        let module = localStorage.getItem("module");
        
        if (window.location.pathname === "/workspace/preparing") {
            navigate("/workspace/preparing");
        } else {
            navigate(module ?? window.location.pathname);
        }
        
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        let parties = await ServiceWrapper.doGet(
            api_base_url + "user/party/active/list",
            null,
            null,
            AuthManager.getGmsToken()
        );
        
        if (parties.errorfound) {
            console.log('[getPartyList] Error found in response');
            return;
        }

        var tplist = parties.data.data;
        if (tplist.length <= 0) {
            return navigate("/workspace/notfound");
        }

        setPartyList(sortByName(tplist));
        _partyList = tplist;
        
        const storedPartyId = localStorage.getItem("party");
        
        if (storedPartyId) {
            const result1 = tplist.filter(
                (item: IParty) => item.id === storedPartyId
            );

            if(result1[0]) {
                _partyList = result1;
            }
        }

        // Update local state for immediate UI feedback
        setSelectedPartyLocal(_partyList[0]);
        appData.setSelectedParty(_partyList[0]);

        if (!skipWorkspaceSwitch) {
            await getIndustryList(_partyList[0].id);
        }
    };

    const getIndustryList = async (partyid: string) => {
        
        const requestKey = 'industry-list';
        const requestVersion = requestVersions.current.getNextVersion(requestKey);
        
        if (operationInProgress.current.industry) {
            return;
        }
        
        operationInProgress.current.industry = true;
        setIsLoadingIndustry(true);
        
        try {
            setParentChannelList([]);
            setEnvironmentList([]);
            setSelectedParentChannel(null);
            setSelectedEnvironment(null);
            
            const api_base_url = AppSettings.GMS_BACKEND_API_URL;
            let industryResponse = await ServiceWrapper.doGet(
                api_base_url + "party/" + partyid + "/industry/list/active",
                null,
                null,
                AuthManager.getGmsToken()
            );
            
            
            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                console.log('[getIndustryList] Not latest version, discarding');
                return;
            }
            
            if (industryResponse.errorfound) {
                console.log('[getIndustryList] Error found in response');
                throw new Error("Industry not found");
            }
            
            var tplist = industryResponse.data.data;
            if (tplist === undefined || tplist.length === 0) {
                setIndustryList([]);
                setSelectedIndustryLocal(null);
                appData.setSelectedIndustry(null);

                localStorage.removeItem("industry");
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setMerchantList([]);

                return navigate("/workspace/industry/notfound");
            }
            
            const storedIndustry = localStorage.getItem("industry");
            const storedParty = localStorage.getItem("party");
            
            let _industryUpdateData = tplist[0];
            let _partyId = partyid;
            let _industryId = tplist[0].id;
            
            if(storedIndustry && storedParty) {
                let finalData = JSON.parse(storedIndustry.replace(/\\/g, ""));
                if (!finalData) return;
                const test = tplist.filter(
                    (item: { id: string }) => item.id === finalData.id
                );
                
                if (test[0]) {
                    _industryUpdateData = test[0];
                    _partyId = storedParty;
                    _industryId = finalData.id;
                }
            }
            
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setIndustryList(sortByName(tplist));
                // Update local state for immediate UI feedback
                setSelectedIndustryLocal(_industryUpdateData);
                appData.update_industry(_industryUpdateData);

                // Mark industry loading as complete before loading channels
                // This ensures domain dropdown updates before channel dropdowns
                setIsLoadingIndustry(false);

                let channelsLoaded = false;
                if (!isSwitchingWorkspace.current) {
                    // Allow React state updates to render before loading channels
                    await new Promise(resolve => setTimeout(resolve, 0));
                    channelsLoaded = await getParentChannels(_partyId, _industryId);
                }

                const storedParentChannel = readWorkspaceKey("selectedParentChannel");
                const storedEnvironment = readWorkspaceKey("selectedEnvironment");
                let parentChannelId = '';
                let channelId = '';

                if (storedParentChannel) {
                    const parsedChannel = JSON.parse(storedParentChannel);
                    parentChannelId = parsedChannel.id || '';
                }

                if (storedEnvironment) {
                    const parsedEnv = JSON.parse(storedEnvironment);
                    channelId = parsedEnv.id || '';
                }

                isSwitchingWorkspace.current = true;

                const switchSuccess = await switchWorkspace(
                    _partyId,
                    _industryId,
                    parentChannelId,
                    channelId,
                    "Please wait while we prepare your workspace..."
                );

                // Redirect based on whether workspace switch succeeded AND channels were loaded.
                // A successful switch must never preserve a workspace-not-ready route (not just
                // /workspace/preparing — a stale /workspace/industry/notfound etc. would otherwise
                // keep showing its error over a now-healthy session).
                if (switchSuccess && channelsLoaded) {
                    navigate(isWorkspaceNotReadyRoute(window.location.pathname) ? "/gms/performance" : window.location.pathname);
                } else if (!channelsLoaded) {
                    // No channels found - redirect to no permission page
                    navigate("/workspace/module/permission/denied");
                }

                isSwitchingWorkspace.current = false;
            }
            
        } catch (err) {
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setSelectedIndustryLocal(null);
                appData.setSelectedIndustry(null);
                setIndustryList([]);
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);

                localStorage.removeItem("industry");
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setMerchantList([]);

                isSwitchingWorkspace.current = false;

                // Stay on preparing page when error occurs
                if (window.location.pathname !== "/workspace/preparing") {
                    navigate("/workspace/preparing");
                }
            }
        } finally {
            operationInProgress.current.industry = false;
            setIsLoadingIndustry(false);
        }
    };

    const getParentChannels = async (partyId: string, industryId: string, shouldSwitch: boolean = false): Promise<boolean> => {

        const requestKey = `channels-${partyId}-${industryId}`;
        const requestVersion = requestVersions.current.getNextVersion(requestKey);

        if (operationInProgress.current.channel) {
            return false;
        }

        operationInProgress.current.channel = true;
        setIsLoadingChannel(true);

        try {
            setEnvironmentList([]);
            setSelectedEnvironment(null);

            const parentResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${partyId}/industry/${industryId}/parent_channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );

            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                console.log('[getParentChannels] Not latest version, discarding');
                return false;
            }

            const response = parentResponse?.data;

            if (response && response.data && Array.isArray(response.data)) {
                if (response.data.length === 0) {
                    setParentChannelList([]);
                    setEnvironmentList([]);
                    setSelectedParentChannel(null);
                    setSelectedEnvironment(null);
                    clearWorkspaceKey("selectedParentChannel");
                    clearWorkspaceKey("selectedEnvironment");
                    appData.setChannelKey("");
                    appData.setChannelName("");
                    appData.setMerchantList([]);
                    return false;
                }

                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setParentChannelList(sortByName(response.data));

                    const storedChannel = readWorkspaceKey("selectedParentChannel");
                    let channelToSelect = null;

                    if (storedChannel) {
                        try {
                            const parsedChannel = JSON.parse(storedChannel);
                            const foundChannel = response.data.find((ch: any) => ch.id === parsedChannel.id);
                            if (foundChannel) {
                                channelToSelect = foundChannel;
                            } else {
                                clearWorkspaceKey("selectedParentChannel");
                                clearWorkspaceKey("selectedEnvironment");
                            }
                        } catch (e) {
                            console.error('[getParentChannels] Error parsing stored channel:', e);
                            clearWorkspaceKey("selectedParentChannel");
                            clearWorkspaceKey("selectedEnvironment");
                        }
                    }

                    if (!channelToSelect && response.data.length > 0) {
                        channelToSelect = response.data[0];
                        writeWorkspaceKey("selectedParentChannel", JSON.stringify(channelToSelect));
                    }

                    if (channelToSelect) {
                        setSelectedParentChannel(channelToSelect);

                        // Mark channel loading as complete BEFORE loading environments
                        // This ensures channel dropdown shows selected value while environments load
                        setIsLoadingChannel(false);
                        operationInProgress.current.channel = false;

                        const envSuccess = await getEnvironments(channelToSelect.id, shouldSwitch, partyId);
                        return envSuccess;
                    }
                }
            } else {
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    if (response?.message) {
                        toast.error(response.message);
                    }
                    setParentChannelList([]);
                    setEnvironmentList([]);
                    setSelectedParentChannel(null);
                    setSelectedEnvironment(null);
                    clearWorkspaceKey("selectedParentChannel");
                    clearWorkspaceKey("selectedEnvironment");
                    appData.setChannelKey("");
                    appData.setChannelName("");
                    appData.setMerchantList([]);
                }
            }
            return false;
        } catch (error) {
            console.error('[getParentChannels] Error:', error);
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setParentChannelList([]);
                setEnvironmentList([]);
                setSelectedParentChannel(null);
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedParentChannel");
                clearWorkspaceKey("selectedEnvironment");
                appData.setChannelKey("");
                appData.setChannelName("");
                appData.setMerchantList([]);
            }
            return false;
        } finally {
            // Only set these if not already set (for error/early return cases)
            if (operationInProgress.current.channel) {
                operationInProgress.current.channel = false;
                setIsLoadingChannel(false);
            }
        }
    };

    const getEnvironments = async (parentChannelId: string, shouldSwitch: boolean = false, partyId?: string): Promise<boolean> => {

        const requestKey = `environments-${parentChannelId}`;
        const requestVersion = requestVersions.current.getNextVersion(requestKey);

        if (operationInProgress.current.environment) {
            return false;
        }

        operationInProgress.current.environment = true;
        setIsLoadingEnvironment(true);

        try {
            const targetPartyId = partyId || appData.selectedParty?.id;

            if (!targetPartyId) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                return false;
            }

            const environementResponse = await ServiceWrapper.doGet(
                `${AppSettings.GMS_BACKEND_API_URL}list/party/${targetPartyId}/parent_channel/${parentChannelId}/channel`,
                null,
                null,
                AuthManager.getGmsToken()
            );


            if (!requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                return false;
            }

            const response = environementResponse?.data;

            if (response && response.data && Array.isArray(response.data)) {
                if (response.data.length === 0) {
                    setEnvironmentList([]);
                    setSelectedEnvironment(null);
                    clearWorkspaceKey("selectedEnvironment");
                    return false;
                }

                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setEnvironmentList(sortByName(response.data));
                    appData.setEnvironmentList?.(sortByName(response.data));

                    const storedEnv = readWorkspaceKey("selectedEnvironment");
                    let envToSelect = null;

                    if (storedEnv) {
                        try {
                            const parsedEnv = JSON.parse(storedEnv);
                            const foundEnv = response.data.find((env: any) => env.id === parsedEnv.id);
                            if (foundEnv) {
                                envToSelect = foundEnv;
                            } else {
                                clearWorkspaceKey("selectedEnvironment");
                            }
                        } catch (e) {
                            console.error('[getEnvironments] Error parsing stored environment:', e);
                            clearWorkspaceKey("selectedEnvironment");
                        }
                    }

                    if (!envToSelect && response.data.length > 0) {
                        envToSelect = response.data[0];
                        writeWorkspaceKey("selectedEnvironment", JSON.stringify(envToSelect));
                    }

                    if (envToSelect) {
                        setSelectedEnvironment(envToSelect);
                        appData.setChannelKey(envToSelect?.key ?? '');
                        appData.setChannelName(envToSelect?.name ?? '');
                        appData.setEnableConfigure(envToSelect?.enable_configure ?? false);
                        appData.setEnvPlatform?.(envToSelect?.platform ?? '');

                        if (shouldSwitch && appData.selectedParty && appData.selectedIndustry) {
                            await switchWorkspace(
                                appData.selectedParty.id,
                                appData.selectedIndustry.id,
                                parentChannelId,
                                envToSelect.id,
                                "Updating workspace..."
                            );
                        }
                        return true;
                    }
                }
            } else {
                if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                    setEnvironmentList([]);
                    setSelectedEnvironment(null);
                    clearWorkspaceKey("selectedEnvironment");
                }
            }
            return false;
        } catch (error) {
            console.error('[getEnvironments] Error:', error);
            if (requestVersions.current.isLatestVersion(requestKey, requestVersion)) {
                setEnvironmentList([]);
                setSelectedEnvironment(null);
                clearWorkspaceKey("selectedEnvironment");
            }
            return false;
        } finally {
            operationInProgress.current.environment = false;
            setIsLoadingEnvironment(false);
        }
    };

    const switchWorkspace = async (
        party_id: string,
        industry_id: string,
        parent_channel_id: string = '',
        channel_id: string = '',
        dialogmessage: string = "Switching workspace...",
        initnew: boolean = false
    ) => {
        const api_base_url = AppSettings.GMS_BACKEND_API_URL;
        const psresp = await ServiceWrapper.doPost(
            api_base_url + "party/switch",
            { 
                party_id: party_id, 
                industry_id: industry_id, 
                app_name: "GMS", 
                parent_channel_id: parent_channel_id || '', 
                channel_id: channel_id || ''
            } as any,
            null,
            AuthManager.getGmsToken()
        );

        if (!psresp.errorfound && psresp.data.done) {
            localStorage.setItem(
                "parties",
                JSON.stringify(psresp.data.user_object.profile.parties || "")
            );
            localStorage.setItem(
                "current_party_id",
                JSON.stringify(psresp.data.user_object.current_party_id || "")
            );
        }

        try{
            // The dialog renders this as HTML (BootBoxManager.dialog -> bootbox message), so escape
            // any server-supplied text before interpolating it.
            const asHtml = (detail: string) =>
                "<p>We are Sorry, We could not set or switch the workspace for you.</p><p><strong>" +
                detail.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") +
                "</strong></p>";

            if (psresp.errorfound)
                // ServiceWrapper sets data:null when errorfound is true, so the old
                // `psresp.data.message` here threw a TypeError and the dialog showed
                // "Cannot read properties of null" instead of the actual failure.
                throw new Error(asHtml(
                    psresp.errorbj?.message ?? "The request to switch workspace failed."
                ))

            // /party/switch answers 200 with done:false and a specific reason ("Party is not
            // valid", "Industry is not associated to this merchant", ...). Surface it — the old
            // generic "There was a technical error." discarded the only diagnostic available.
            if(!psresp.data?.done)
                throw new Error(asHtml(
                    psresp.data?.message ?? "There was a technical error."
                ))

            if (partyList && partyList.length > 0) {
                const partyDetails = partyList?.filter(
                    (item: { id: string }) => item.id === party_id
                );

                if (partyDetails && partyDetails[0]) {
                    setSelectedPartyLocal(partyDetails[0]);
                    appData.update_party(partyDetails[0]);
                }
            }
            
            await AuthManager.setGmsToken(psresp.data.access_token);
            if (!Utils.isUndefinedOrNull(psresp.data.user_object))
                await AuthManager.setAuthUser(psresp.data.user_object);
            const pm = new PermissionsManager();
            await pm.ProcessPermissions();
            return true;
        }
        catch(err: any) {
            console.error('[switchWorkspace] Error:', err);
            BootBoxManager.dialog(
                "Workspace switch Failed",
                err.message
            );
            navigate("/workspace/industry/notfound");
            return false;
        }
    };

    const handlePartyChange = async (item: any) => {
        if (operationInProgress.current.party || isLoadingIndustry) {
            return;
        }

        analyticsContext?.clearAllTheFields();
        appData.setChannelKey("");
        appData.setChannelName("");
        appData.setMerchantList([]);
        if(!item) return toast.error("Party not found")

        // Skip if same party is selected
        if (item.id === appData.selectedParty?.id) return;

        operationInProgress.current.party = true;
        isManualPartyChange.current = true;

        // Update local state FIRST for immediate UI feedback
        setSelectedPartyLocal(item);

        // Then update context state and localStorage
        appData.setSelectedParty(item);
        localStorage.setItem("party", item.id);
        localStorage.setItem("current_party_id", JSON.stringify(item.id || ""));

        // Clear dependent local state immediately
        setSelectedIndustryLocal(null);
        setIndustryList([]);
        setParentChannelList([]);
        setEnvironmentList([]);
        setSelectedParentChannel(null);
        setSelectedEnvironment(null);

        // Clear dependent localStorage
        localStorage.removeItem("industry");
        clearWorkspaceKey("selectedParentChannel");
        clearWorkspaceKey("selectedEnvironment");

        // Navigate to preparing page
        navigate("/workspace/preparing");

        await getIndustryList(item.id);

        isManualPartyChange.current = false;
        operationInProgress.current.party = false;
    };

    const handleIndustryChange = async (item: any) => {
        if (operationInProgress.current.industry || isLoadingChannel) {
            return;
        }

        analyticsContext?.clearAllTheFields();
        appData.setChannelKey("");
        appData.setChannelName("");
        appData.setMerchantList([]);

        // Skip if same domain is selected
        if (item.id === appData.selectedIndustry?.id) return;

        operationInProgress.current.industry = true;
        isManualIndustryChange.current = true;

        // Update local state FIRST for immediate UI feedback
        setSelectedIndustryLocal(item);

        // Then update context state and localStorage
        appData.update_industry(item);
        localStorage.setItem("industry", JSON.stringify(item));
        localStorage.setItem("party", appData.selectedParty.id);

        // Clear dependent local state immediately
        setParentChannelList([]);
        setEnvironmentList([]);
        setSelectedParentChannel(null);
        setSelectedEnvironment(null);

        // Clear dependent localStorage
        clearWorkspaceKey("selectedParentChannel");
        clearWorkspaceKey("selectedEnvironment");

        // Navigate to preparing page
        navigate("/workspace/preparing");

        const channelsLoaded = await getParentChannels(appData.selectedParty.id, item.id, true);

        // Redirect based on whether channels were loaded
        if (channelsLoaded) {
            navigate("/gms/performance");
        } else {
            // No channels found - redirect to no permission page
            navigate("/workspace/module/permission/denied");
        }

        isManualIndustryChange.current = false;
        operationInProgress.current.industry = false;
    };

    const handleParentChannelChange = async (item: any) => {
        if (operationInProgress.current.channel || isLoadingEnvironment) {
            return;
        }

        if (!item) return;

        // Skip if same parent channel is selected
        if (item.id === selectedParentChannel?.id) return;

        operationInProgress.current.channel = true;

        // Update channel state and localStorage first
        setSelectedParentChannel(item);
        writeWorkspaceKey("selectedParentChannel", JSON.stringify(item));

        // Navigate to preparing page and allow state to render
        navigate("/workspace/preparing");
        await new Promise(resolve => setTimeout(resolve, 0));

        // Now clear dependent state
        clearWorkspaceKey("selectedEnvironment");
        setEnvironmentList([]);
        setSelectedEnvironment(null);

        const envSuccess = await getEnvironments(item.id, true, appData.selectedParty?.id);

        // Redirect based on whether environments were loaded
        if (envSuccess) {
            navigate("/gms/performance");
        } else {
            // No environments found - redirect to no permission page
            navigate("/workspace/module/permission/denied");
        }

        operationInProgress.current.channel = false;
    };

    const handleEnvironmentChange = async (item: any) => {
        if (operationInProgress.current.environment) {
            return;
        }

        if (!item) return;

        // Skip if same environment is selected
        if (item.id === selectedEnvironment?.id) return;

        operationInProgress.current.environment = true;

        // Show preparing page while switching workspace
        navigate("/workspace/preparing");

        setSelectedEnvironment(item);
        writeWorkspaceKey("selectedEnvironment", JSON.stringify(item));

        appData.setChannelKey(item?.key ?? '');
        appData.setChannelName(item?.name ?? '');
        appData.setEnableConfigure(item?.enable_configure ?? false);
        appData.setEnvPlatform?.(item?.platform ?? '');

        if (appData.selectedParty && appData.selectedIndustry && selectedParentChannel) {
            const success = await switchWorkspace(
                appData.selectedParty.id,
                appData.selectedIndustry.id,
                selectedParentChannel.id,
                item.id,
                "Switching to environment: " + item.name
            );

            if (success) {
                toast.success(`Switched to environment "${item.name}"`);
                navigate("/gms/performance");
            }
            // If switch fails, user stays on preparing page (switchWorkspace handles error display)
        }

        operationInProgress.current.environment = false;
    };

    const getChannelList = async () => {
        try {
            const response = await getMerchantsBasedOnPartyAndIndustry(current_party_id, industryId);
            if (response?.data?.done) {
                const channels = response?.data.data?.map((res: any) => ({
                    label: res?.user_name,
                    value: res?.merchant_key
                })) ?? [];
                // Sort channels by label (case insensitive)
                const sortedChannels = [...channels].sort((a: any, b: any) =>
                    (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase())
                );
                setChannelList(sortedChannels)
                setSelectedChannel(channels[0]?.value)
                // NOTE: do NOT write appData.channelKey/channelName here. This is the Analytics
                // *merchant* list, which is a different list from the Environment list that owns
                // channelKey. Writing channels[0] here used to clobber the selected Environment's
                // channel_key (it always picked the first merchant, regardless of the Environment
                // the user had selected), so Guide/Commend/Analytics could end up querying a
                // different channel than the one shown in the Environment picker.
                // channelKey is owned solely by refreshEnvironments()/handleEnvironmentChange().

                // Store full merchant data in AppContext for analytics to reuse
                appData.setMerchantList(response?.data?.data ?? []);
            }
        } catch (error) {
            console.error(error);
        }
    }

    // Analytics merchant filter only. Deliberately does not touch appData.channelKey —
    // that field tracks the selected Environment (see getChannelList note above).
    const handleChannelChange = (e:string)=>{
        setSelectedChannel(e)
    }

    useEffect(() => {

        if (isManualPartyChange.current || isInitialLoad.current || partyList.length <= 0) return;

        // Skip if hierarchy was already loaded by refreshAll to prevent duplicate loading
        // Don't reset the flag here - let refreshAll control it
        if (hierarchyLoadedByRefresh.current) {
            return;
        }

        async function fetchIndustryList() {
            let _party = partyList
            const storedPartyId = localStorage.getItem("party");

            if (storedPartyId) {
                const result1 = partyList.filter(
                    (item: any) => item.id === storedPartyId
                ) as any;

                if(result1[0]){
                    setIndustryList([]);
                    _party = result1
                }

                // Update local state for immediate UI feedback
                setSelectedPartyLocal(_party[0]);
                appData.setSelectedParty(_party[0]);
                await getIndustryList(_party[0].id);
            }
        }
        fetchIndustryList()
    }, [partyList])

    useEffect(() => {
        const prevParty = prevCurrentPartyId.current;
        const prevIndustry = prevIndustryId.current;
        prevCurrentPartyId.current = current_party_id;
        prevIndustryId.current = industryId;
        // Skip only on the very first effect run (refs are undefined until the first render
        // completes). After that prevParty/prevIndustry hold the previous render's values —
        // null is a real state (no industry selected yet), so null→value transitions must
        // not be swallowed.
        if (prevParty === undefined || prevIndustry === undefined) return;
        if (current_party_id && industryId) {
            // Only refresh the Analytics merchant list here. This effect must NOT clear
            // channelKey/channelName: it fires on the same party/industry transition that the
            // login + environment bootstrap (refreshDomains -> refreshChannels ->
            // refreshEnvironments) is driving, so clearing them raced the environment write and
            // could leave Guide/Commend with an empty or stale channel_key.
            getChannelList();
        }
    }, [current_party_id, industryId]);

    React.useEffect(() => {
        return () => {
            requestVersions.current.clearAll();
        };
    }, []);
    
    return {
        partyList,
        selectedParty,
        handlePartyChange,
        industryList,
        selectedIndustry,
        handleIndustryChange,
        getUserName,
        channelList,
        selectedChannel,
        handleChannelChange,
        parentChannelList,
        selectedParentChannel,
        handleParentChannelChange,
        environmentList,
        selectedEnvironment,
        handleEnvironmentChange,
        refreshParties,
        refreshDomains,
        refreshChannels,
        refreshEnvironments,
        isLoadingParty,
        isLoadingIndustry,
        isLoadingChannel,
        isLoadingEnvironment
    }
}

export default useSearchBar;

class RequestVersionManager {
    private versions: Map<string, number> = new Map();
    
    getNextVersion(key: string): number {
        const currentVersion = this.versions.get(key) || 0;
        const nextVersion = currentVersion + 1;
        this.versions.set(key, nextVersion);
        return nextVersion;
    }
    
    isLatestVersion(key: string, version: number): boolean {
        return this.versions.get(key) === version;
    }
    
    clear(key: string) {
        this.versions.delete(key);
    }
    
    clearAll() {
        this.versions.clear();
    }
}